import { useInfiniteQuery, useQueryClient, UseInfiniteQueryOptions, InfiniteData } from '@tanstack/react-query';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { getSchedules } from 'src/api/getSchedules/getSchedules';
import { ScheduleStatus, ValidationStatus } from 'src/api/getSchedules/types';
import type { IGetSchedulesResponse } from 'src/api/getSchedules/types';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { QueryKeys } from 'src/hooks/QueryKeys';

export const VALIDATION_POLL_INTERVAL_MS = 5000;

const scheduleQueryOptions = (status: ScheduleStatus) => ({
  queryKey: [getBrandId(), QueryKeys.schedules, status],
  queryFn: async ({ pageParam }: { pageParam?: string }) => getSchedules('15', pageParam, status),
});

export const useSchedules = (status: ScheduleStatus, options?: UseInfiniteQueryOptions<IGetSchedulesResponse>) => {
  const { showSnackbar } = useSnackbar();

  return useInfiniteQuery<IGetSchedulesResponse>({
    ...scheduleQueryOptions(status),
    getNextPageParam: (lastPage) => lastPage.paging.next ?? undefined,
    refetchOnWindowFocus: false,
    refetchInterval: (data: InfiniteData<IGetSchedulesResponse> | undefined) => {
      const isAnyValidating = data?.pages
        .flatMap((p) => p.data)
        .some((s) => s.validation.status === ValidationStatus.VALIDATING);
      return isAnyValidating ? VALIDATION_POLL_INTERVAL_MS : false;
    },
    onError: () => showSnackbar({ status: SnackbarStatus.ERROR, message: 'Internal Server Error' }),
    ...options,
  });
};

export const usePrefetchSchedules = (status: ScheduleStatus) => {
  const queryClient = useQueryClient();
  return () => queryClient.prefetchInfiniteQuery(scheduleQueryOptions(status));
};
